import { normalizeContentfulText } from '@sola/ark-skippy/utils/text-utils.tsx';
import { keys } from '@sola/contentful-model/keys.ts';
import { getArticlePath } from '@sola/contentful-model/lib/article.ts';
import type { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.ts';
import type {
  ContentResponseDto,
  JsonFieldFundSettings,
  JsonLink,
  LinkedContentResponseDto,
  Media,
  ReferencedContentResponseDto,
} from '@sola/schema/lib/contentful.ts';
import { LinkType } from '@sola/schema/lib/contentful.ts';
import type { Link } from '@sola/schema/lib/schema.ts';
import {
  FlexibleContentProps,
  FundProps,
  MediaProps,
} from '@sola/schema/lib/schema.ts';
import { removeNullValues } from '@sola/utils/object.ts';
import { isAbsoluteInternalLink, slashes } from '@sola/utils/url.ts';

import { resolveAbsolutePrefix } from './logic.tsx';

export const convertContentResponseToLink = (
  props?: JsonLink | null,
  linkedContent?: LinkedContentResponseDto,
): Link | undefined => {
  if (!props) {
    return undefined;
  }
  const title = props?.type !== LinkType.enum.none && props?.title;

  if (props?.type === LinkType.enum.external) {
    const isInternal = isAbsoluteInternalLink(props.externalUrl);
    return {
      link: props.externalUrl || '#invalid-link',
      title: title || '',
      target: isInternal ? '' : '_blank',
      icon: props.icon || '',
      iconPlacement: props.iconPlacement,
    };
  }
  if (props?.type === LinkType.enum.download) {
    const linkedContentItem = linkedContent?.items?.find(
      (item) => item?.sys?.id === props.download.id,
    );

    const path = `/file/${linkedContentItem?.sys.id}`;
    const siteRootUrl = resolveAbsolutePrefix(keys.contentTypes.downloads);
    return {
      link: `${siteRootUrl}${path}` || '#unknown-link',
      title: title || '',
      target: '_blank',
      icon: props.icon || '',
      iconPlacement: props.iconPlacement,
    };
  }
  if (props?.type === LinkType.enum.internal) {
    const linkedContentItem = linkedContent?.items?.find(
      (item) => item?.sys?.id === props.entry.id,
    );

    if (!linkedContentItem) {
      console.error(
        `[contentful-react] Internal link references entry "${props.entry.id}" (type: "${props.entry.type}") but it was not found in linkedContent. This produces a "/undefined" path in the link for "${title}". Check that the entry is published and included in the query.`,
        props,
      );
    }

    let path = linkedContentItem?.path
      ? slashes(linkedContentItem.path, 'both')
      : '/#invalid-link';
    let isDownload = false;

    const siteRootUrl = resolveAbsolutePrefix(props.entry.type);

    if (
      linkedContentItem &&
      props.entry.type === keys.contentTypes.article &&
      linkedContentItem.slug
    ) {
      path = slashes(
        getArticlePath(
          linkedContentItem.slug,
          linkedContentItem.taxonomyArticleType as ArticleTypesEnum,
        ),
        'both',
      );
    }
    if (linkedContentItem && props.entry.type === keys.contentTypes.downloads) {
      path = `/file/${props.entry.id}`;
      isDownload = true;
    }

    if (props.parameters) {
      path += `?${props.parameters}`;
    }

    if (props.anchor) {
      path += `#${props.anchor}`;
    }

    return {
      link: path ? `${siteRootUrl}${path}` : '#unknown-link',
      title: title || '',
      target: isDownload ? '_blank' : '',
      icon: props.icon || '',
      iconPlacement: props.iconPlacement,
    };
  }
  if (props?.type === LinkType.enum.anchor) {
    return {
      link: `#${props.anchor}`,
      title: title || '',
      icon: props.icon || '',
      iconPlacement: props.iconPlacement,
    };
  }
  if (props?.type === LinkType.enum.mailto) {
    return {
      link: `mailto:${props.mailto}`,
      title: title || '',
      icon: props.icon || '',
      iconPlacement: props.iconPlacement,
    };
  }
  if (props?.type === LinkType.enum.tel) {
    return {
      link: `tel:${props.tel}`,
      title: title || '',
      icon: props.icon || '',
      iconPlacement: props.iconPlacement,
    };
  }

  return undefined;
};

export const convertMediaResponseToMedia = (source: Media) => {
  if (!source) {
    return;
  }

  // Use the video's own description if available, otherwise fall back to the
  // media caption so VideoObject schema always has a description when possible.
  const video = source.video
    ? {
        ...source.video,
        description: source.video.description || source.caption || undefined,
      }
    : undefined;

  const media = {
    caption: source.caption || undefined,
    image: source.image
      ? {
          ...source.image,
          alt: source.altText || '',
          focus: source.imageFocalPoint?.focalPoint || undefined,
          ratio: source.imageFocalPoint?.ratio || undefined,
        }
      : undefined,
    video,
  };

  return media;
};

export const convertContentResponseToFund = (
  props?: JsonFieldFundSettings | null,
  referencedContent?: ReferencedContentResponseDto,
): FundProps | undefined => {
  if (!props || !props.entry) {
    return undefined;
  }

  const fund = referencedContent?.items?.find(
    (item) => item?.sys?.id === props.entry?.id,
  );

  const catalogAmount =
    props?.suggestedAmount ??
    props?.suggestedAmountsOneTime?.find((item) => item.isDefault)?.value ??
    props?.suggestedAmountsMonthly?.find((item) => item.isDefault)?.value ??
    props?.suggestedAmountsAnnually?.find((item) => item.isDefault)?.value;

  const fundData = {
    id: props.entry?.id,
    ...fund,
    suggestedAmount: props.suggestedAmount || undefined,
    suggestedAmountsOneTime: props.suggestedAmountsOneTime || undefined,
    suggestedAmountsMonthly: props.suggestedAmountsMonthly || undefined,
    suggestedAmountsAnnually: props.suggestedAmountsAnnually || undefined,
    suggestedDonateFrequency: props.suggestedDonateFrequency || undefined,
    catalogAmount: catalogAmount || undefined,
  };

  const cleanedFundData = removeNullValues(fundData);

  return FundProps.parse(cleanedFundData);
};

export const convertContentResponseToFlexibleContent = (
  source: ContentResponseDto,
): FlexibleContentProps | undefined => {
  if (!source) {
    return;
  }

  const media = convertMediaResponseToMedia(source.media as Media);
  const parsedMedia = MediaProps.safeParse(media);

  const mediaLogo = convertMediaResponseToMedia(source.mediaLogo as Media);
  const parsedMediaLogo = MediaProps.safeParse(mediaLogo);

  const displayDate = source.displayDate || source.sys.publishedAt;

  return FlexibleContentProps.parse({
    title: normalizeContentfulText(source.title || undefined),
    subtitle: normalizeContentfulText(source.subtitle || undefined),
    icon: source.icon || undefined,
    badge: source.badge || undefined,
    quotation: source.quotation || undefined,
    logo: source.logo || undefined,
    primaryCTA:
      convertContentResponseToLink(
        source.primaryCTA || source.primaryCta,
        source.linkedContentCollection,
      ) || undefined,
    secondaryCTA:
      convertContentResponseToLink(
        source.secondaryCTA || source.secondaryCta,
        source.linkedContentCollection,
      ) || undefined,
    tertiaryCTA:
      convertContentResponseToLink(
        source.tertiaryCTA || source.tertiaryCta,
        source.linkedContentCollection,
      ) || undefined,
    media: parsedMedia?.data,
    mediaLogo: parsedMediaLogo?.data,
    displayDate: displayDate || undefined,
    fund:
      convertContentResponseToFund(
        source.fund,
        source.referencedContentCollection,
      ) || undefined,
  });
};
